import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  Plus,
  Workflow,
  Edit3,
  Trash2,
  Copy,
  CheckCircle2,
  CircleOff,
  Zap,
} from 'lucide-react';
import {
  useTenantFlowsAll,
  useDeleteFlow,
  useCloneFlow,
  findFormsUsingFlow,
  type TenantFlow,
  type TriggerType,
} from '~/lib/flows-queries';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import { CloneDialog } from '~/components/CloneDialog/CloneDialog';
import { Tooltip } from '~/components/Tooltip/Tooltip';
import { useTenantContext } from '~/lib/tenantContext';
import styles from './_authed.automations.templates.module.css';

export const Route = createFileRoute('/_authed/automations/flows')({
  component: FlowsListPage,
});

const TRIGGER_LABELS: Record<TriggerType, string> = {
  lead_created: 'Oportunidad creada',
  form_submitted: 'Formulario enviado',
  lead_stage_changed: 'Cambio de stage',
  manual: 'Manual',
};

function FlowsListPage() {
  const navigate = useNavigate();
  const { tenants, selectedTenantId } = useTenantContext();
  const { data: flows, isLoading } = useTenantFlowsAll();
  const del = useDeleteFlow();
  const clone = useCloneFlow();
  const confirm = useConfirm();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [cloning, setCloning] = useState<TenantFlow | null>(null);
  const [triggerFilter, setTriggerFilter] = useState<'all' | TriggerType>('all');
  const [actionError, setActionError] = useState<string | null>(null);

  function handleClone(targetTenant: string) {
    if (!cloning) return;
    clone.mutate(
      { source: cloning, targetTenant },
      {
        onSuccess: ({ flow }) => {
          setCloning(null);
          navigate({ to: '/automations/flows/$flowId', params: { flowId: flow.id } });
        },
      },
    );
  }

  // Filtra siempre por el tenant activo del contexto (los flujos son per-tenant).
  const filtered = useMemo(() => {
    if (!flows) return [];
    return flows.filter((f) => {
      if (selectedTenantId && f.tenant !== selectedTenantId) return false;
      if (triggerFilter !== 'all' && f.trigger_type !== triggerFilter) return false;
      return true;
    });
  }, [flows, selectedTenantId, triggerFilter]);

  async function handleDelete(flow: TenantFlow) {
    let usedBy: { id: string; name: string }[] = [];
    try { usedBy = await findFormsUsingFlow(flow.id); } catch { /* ignore */ }
    const ok = await confirm({
      title: `Eliminar flujo "${flow.name}"`,
      description: (
        <>
          {usedBy.length > 0 && (
            <p style={{ marginTop: 0, fontWeight: 600 }}>
              ⚠️ Lo usa{usedBy.length > 1 ? 'n' : ''} {usedBy.length}{' '}
              formulario{usedBy.length > 1 ? 's' : ''} como flujo por defecto:{' '}
              {usedBy.map((f) => `«${f.name}»`).join(', ')}. Esos formularios
              quedarán sin automatización al enviarse.
            </p>
          )}
          Se eliminan también todos sus pasos. Las ejecuciones históricas se
          conservan para auditoría. Acción irreversible.
        </>
      ),
      confirmLabel: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;
    setDeletingId(flow.id);
    setActionError(null);
    del.mutate(flow.id, {
      onError: (err) => setActionError(
        'No se pudo eliminar el flujo: ' + ((err as { message?: string }).message || 'error desconocido'),
      ),
      onSettled: () => setDeletingId(null),
    });
  }

  return (
    <div className={styles.tab}>
      <div className={styles.tabHeader}>
        <Link to="/automations/flows/new" className={styles.primaryBtn}>
          <Plus size={14} aria-hidden /> Nuevo flujo
        </Link>
      </div>

      {actionError && (
        <p role="alert" style={{ background: 'color-mix(in srgb, #dc2626 12%, transparent)', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, fontSize: 14, margin: '0 0 12px' }}>
          {actionError}
        </p>
      )}

      <div className={styles.toolbar}>
        <label className={styles.filterField}>
          <Zap size={12} aria-hidden />
          <span className={styles.filterLabel}>Trigger:</span>
          <select
            className={styles.filterSelect}
            value={triggerFilter}
            onChange={(e) => setTriggerFilter(e.target.value as 'all' | TriggerType)}
          >
            <option value="all">Todos</option>
            <option value="lead_created">Oportunidad creada</option>
            <option value="form_submitted">Formulario enviado</option>
            <option value="lead_stage_changed">Cambio de stage</option>
            <option value="manual">Manual</option>
          </select>
        </label>
        {flows && (
          <span className={styles.countBadge}>
            {filtered.length} flujo{filtered.length === 1 ? '' : 's'} del cliente activo
          </span>
        )}
      </div>

      {isLoading && <div className={styles.empty}>Cargando flujos…</div>}

      {!isLoading && (!flows || flows.length === 0) && (
        <div className={styles.emptyState}>
          <Workflow size={28} aria-hidden style={{ marginBottom: 8, opacity: 0.5 }} />
          <p>
            No hay flujos todavía. Crea el primero con el botón{' '}
            <strong>"Nuevo flujo"</strong>.
          </p>
          <p className="subtle" style={{ marginTop: 8 }}>
            Un flujo conecta un <strong>trigger</strong> (lead creado, form
            enviado, etc.) con una serie de <strong>pasos</strong> (enviar
            email desde una plantilla, mover stage, crear tarea, asignar
            owner, llamar webhook).
          </p>
        </div>
      )}

      {!isLoading && flows && flows.length > 0 && filtered.length === 0 && (
        <div className={styles.emptyState}>
          <p>No hay flujos que coincidan con los filtros.</p>
        </div>
      )}

      {!isLoading && filtered.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Trigger</th>
              <th>Estado</th>
              <th>Última edición</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.map((f) => {
              return (
                <tr key={f.id} className={deletingId === f.id ? styles.rowDeleting : ''}>
                  <td>
                    <Link
                      to="/automations/flows/$flowId"
                      params={{ flowId: f.id }}
                      className={styles.templateName}
                    >
                      <Workflow size={14} aria-hidden style={{ opacity: 0.5 }} />
                      <div>
                        <div>{f.name}</div>
                        {f.description && (
                          <div className="subtle" style={{ fontSize: 12, marginTop: 2 }}>
                            {f.description}
                          </div>
                        )}
                      </div>
                    </Link>
                  </td>
                  <td>
                    <Tooltip
                      content={
                        f.trigger_type === 'lead_stage_changed' || f.trigger_type === 'manual' ? (
                          <>Soporte completo en Sprint F+. Hoy el motor no dispara este tipo.</>
                        ) : f.trigger_type === 'lead_created' ? (
                          <>Dispara cuando se crea una oportunidad nueva en el cliente (cualquier fuente).</>
                        ) : (
                          <>Dispara cuando llega una submission de un formulario gestionado.</>
                        )
                      }
                    >
                      <span className={styles.triggerChip}>
                        {TRIGGER_LABELS[f.trigger_type] ?? f.trigger_type}
                      </span>
                    </Tooltip>
                  </td>
                  <td>
                    {f.is_active ? (
                      <span className={styles.statusActive}>
                        <CheckCircle2 size={12} aria-hidden /> Activo
                      </span>
                    ) : (
                      <Tooltip content="El flow está pausado. Los triggers no lo ejecutarán hasta reactivarlo.">
                        <span className={styles.statusInactive}>
                          <CircleOff size={12} aria-hidden /> Inactivo
                        </span>
                      </Tooltip>
                    )}
                  </td>
                  <td className="subtle">{formatDate(f.updated)}</td>
                  <td className={styles.actions}>
                    <Link
                      to="/automations/flows/$flowId"
                      params={{ flowId: f.id }}
                      className={styles.iconBtn}
                      title="Editar"
                    >
                      <Edit3 size={14} aria-hidden />
                    </Link>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => setCloning(f)}
                      title="Duplicar"
                      aria-label={`Duplicar flujo ${f.name}`}
                    >
                      <Copy size={14} aria-hidden />
                    </button>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => handleDelete(f)}
                      title="Eliminar"
                      aria-label={`Eliminar flujo ${f.name}`}
                    >
                      <Trash2 size={14} aria-hidden />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {cloning && (
        <CloneDialog
          title="Duplicar flujo"
          itemName={cloning.name}
          sourceTenantId={cloning.tenant}
          tenants={tenants}
          isPending={clone.isPending}
          onClose={() => setCloning(null)}
          onClone={handleClone}
          crossTenantNote={
            <>
              Al copiar a otro cliente, las referencias propias del cliente origen
              se vacían: <strong>stage destino</strong>, <strong>owner</strong> y
              el <code>form_id</code> del trigger. Las plantillas globales y el
              texto (destinatarios, tags, URLs) se conservan. Revisa los pasos en
              el editor antes de activarlo.
            </>
          }
        />
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
